import { Box, Palette } from 'lucide-react';
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect
} from 'react';
import { LayersPanel } from '../panels/LayersPanel';
import { ShapePanel } from '../panels/ShapePanel/ShapePanel';

type Position = {
  x: number;
  y: number;
};

type DetachedPanel = {
  content: React.ReactNode;
  position: Position;
  title: string;
  icon: any;
};

type PanelConfig = {
  id: string;
  title: string;
  icon: any;
  component: React.ComponentType<any>;
  defaultSize: number;
  minSize: number;
  maxSize?: number;
};

type LayoutConfig = {
  left: PanelConfig[];
  right: PanelConfig[];
};

type SavedLayoutConfig = {
  left: string[];
  right: string[];
};

type SavedDetachedPanels = Record<
  string,
  {
    position: Position;
    title: string;
  }
>;

type PanelLayoutContextType = {
  leftCollapsed: boolean;
  rightCollapsed: boolean;
  detachedPanels: Record<string, DetachedPanel>;
  draggingPanel: string | null;
  visibleLeftPanels: string[];
  visibleRightPanels: string[];
  hasVisibleLeftPanels: boolean;
  hasVisibleRightPanels: boolean;
  expandedPanel: string | null;
  layoutConfig: LayoutConfig;
  setLeftCollapsed: (collapsed: boolean) => void;
  setRightCollapsed: (collapsed: boolean) => void;
  setExpandedPanel: (id: string | null) => void;
  handleDetach: (
    id: string,
    content: React.ReactNode,
    title: string,
    icon: any
  ) => void;
  handleAttach: (id: string) => void;
  handleMouseDown: (e: React.MouseEvent, id: string) => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  movePanel: (id: string) => void;
  getPanelSide: (id: string) => 'left' | 'right' | null;
};

const PanelLayoutContext = createContext<PanelLayoutContextType | null>(null);

const defaultLayoutConfig: LayoutConfig = {
  left: [],
  right: [
    {
      id: 'shape',
      title: 'Shapes',
      icon: Box,
      component: ShapePanel,
      defaultSize: 50,
      minSize: 15
    },
    {
      id: 'layers',
      title: 'layers',
      icon: Palette,
      component: LayersPanel,
      defaultSize: 50,
      minSize: 15
    }
  ]
};

const LAYOUT_COOKIE_KEY = 'panel-layout-config-2';
const COLLAPSED_STATE_COOKIE_KEY = 'panel-collapsed-state-2';
const DETACHED_PANELS_COOKIE_KEY = 'panel-detached-state-2';

const getAllPanelConfigs = () => {
  const allPanels = [...defaultLayoutConfig.left, ...defaultLayoutConfig.right];
  return allPanels.reduce(
    (acc, panel) => {
      acc[panel.id] = panel;
      return acc;
    },
    {} as Record<string, PanelConfig>
  );
};

export function PanelLayoutProvider({
  children
}: { children: React.ReactNode }) {
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [detachedPanels, setDetachedPanels] = useState<
    Record<string, DetachedPanel>
  >(() => {
    const savedDetachedPanels = null; //Cookies.get(DETACHED_PANELS_COOKIE_KEY);
    if (savedDetachedPanels) {
      try {
        const parsed = JSON.parse(savedDetachedPanels) as SavedDetachedPanels;
        const allPanelConfigs = getAllPanelConfigs();

        return Object.entries(parsed).reduce(
          (acc, [id, data]) => {
            const panelConfig = allPanelConfigs[id];
            if (panelConfig) {
              const PanelComponent = panelConfig.component;
              acc[id] = {
                content: (
                  <PanelComponent
                    id={id}
                    onDetach={() => {}}
                    isDetached={true}
                  />
                ),
                position: data.position,
                title: data.title,
                icon: panelConfig.icon
              };
            }
            return acc;
          },
          {} as Record<string, DetachedPanel>
        );
      } catch (e) {
        console.error('Error parsing saved detached panels:', e);
        return {};
      }
    }
    return {};
  });
  const [draggingPanel, setDraggingPanel] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<string | null>(null);
  const [layoutConfig, setLayoutConfig] = useState<LayoutConfig>(() => {
    const savedLayout = null; //Cookies.get(LAYOUT_COOKIE_KEY);
    if (savedLayout) {
      try {
        const parsed = JSON.parse(savedLayout) as SavedLayoutConfig;
        const allPanelConfigs = getAllPanelConfigs();

        return {
          left: parsed.left.map((id) => allPanelConfigs[id]).filter(Boolean),
          right: parsed.right.map((id) => allPanelConfigs[id]).filter(Boolean)
        };
      } catch (e) {
        console.error('Error parsing saved layout:', e);
        return defaultLayoutConfig;
      }
    }
    return defaultLayoutConfig;
  });
  const dragOffset = useRef({ x: 0, y: 0 });

  // Load collapsed state from cookies
  useEffect(() => {
    const savedCollapsedState = null; //Cookies.get(COLLAPSED_STATE_COOKIE_KEY);
    if (savedCollapsedState) {
      try {
        const { left, right } = JSON.parse(savedCollapsedState);
        setLeftCollapsed(left);
        setRightCollapsed(right);
      } catch (e) {
        console.error('Error parsing saved collapsed state:', e);
      }
    }
  }, []);

  // Save layout config to cookies whenever it changes
  useEffect(() => {
    const savedLayout: SavedLayoutConfig = {
      left: layoutConfig.left.map((panel) => panel.id),
      right: layoutConfig.right.map((panel) => panel.id)
    };
    /*Cookies.set(LAYOUT_COOKIE_KEY, JSON.stringify(savedLayout), {
      expires: 365
    });*/
  }, [layoutConfig]);

  // Save collapsed state to cookies whenever it changes
  useEffect(() => {
    /*Cookies.set(
      COLLAPSED_STATE_COOKIE_KEY,
      JSON.stringify({ left: leftCollapsed, right: rightCollapsed }),
      { expires: 365 }
    );*/
  }, [leftCollapsed, rightCollapsed]);

  // Save detached panels state to cookies whenever it changes
  useEffect(() => {
    const savedDetachedPanels: SavedDetachedPanels = Object.entries(
      detachedPanels
    ).reduce((acc, [id, panel]) => {
      acc[id] = {
        position: panel.position,
        title: panel.title
      };
      return acc;
    }, {} as SavedDetachedPanels);
    /*Cookies.set(
      DETACHED_PANELS_COOKIE_KEY,
      JSON.stringify(savedDetachedPanels),
      { expires: 365 }
    );*/
  }, [detachedPanels]);

  const handleDetach = (
    id: string,
    content: React.ReactNode,
    title: string,
    icon: any
  ) => {
    setDetachedPanels((prev) => ({
      ...prev,
      [id]: {
        content,
        position: { x: 100, y: 100 },
        title,
        icon
      }
    }));
  };

  const handleAttach = (id: string) => {
    setDetachedPanels((prev) => {
      const newPanels = { ...prev };
      delete newPanels[id];
      return newPanels;
    });
  };

  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    const panel = detachedPanels[id];
    dragOffset.current = {
      x: e.clientX - panel.position.x,
      y: e.clientY - panel.position.y
    };
    setDraggingPanel(id);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (draggingPanel) {
      setDetachedPanels((prev) => ({
        ...prev,
        [draggingPanel]: {
          ...prev[draggingPanel],
          position: {
            x: e.clientX - dragOffset.current.x,
            y: e.clientY - dragOffset.current.y
          }
        }
      }));
    }
  };

  const handleMouseUp = () => {
    setDraggingPanel(null);
  };

  const getPanelSide = (id: string): 'left' | 'right' | null => {
    if (layoutConfig.left.some((p) => p.id === id)) return 'left';
    if (layoutConfig.right.some((p) => p.id === id)) return 'right';
    return null;
  };

  const movePanel = (id: string) => {
    const currentSide = getPanelSide(id);
    if (!currentSide) return;

    const targetSide = currentSide === 'left' ? 'right' : 'left';
    const panel = layoutConfig[currentSide].find((p) => p.id === id);

    /*if (panel) {
      setLayoutConfig((prev) => ({
        [currentSide]: prev[currentSide].filter((p) => p.id !== id),
        [targetSide]: [...prev[targetSide], panel]
      }));
    }*/
  };

  const visibleLeftPanels = layoutConfig.left
    .filter((panel) => !detachedPanels[panel.id])
    .map((panel) => panel.id);

  const visibleRightPanels = layoutConfig.right
    .filter((panel) => !detachedPanels[panel.id])
    .map((panel) => panel.id);

  const hasVisibleLeftPanels = visibleLeftPanels.length > 0;
  const hasVisibleRightPanels = visibleRightPanels.length > 0;

  return (
    <PanelLayoutContext.Provider
      value={{
        leftCollapsed,
        rightCollapsed,
        detachedPanels,
        draggingPanel,
        visibleLeftPanels,
        visibleRightPanels,
        hasVisibleLeftPanels,
        hasVisibleRightPanels,
        expandedPanel,
        layoutConfig,
        setLeftCollapsed,
        setRightCollapsed,
        setExpandedPanel,
        handleDetach,
        handleAttach,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        movePanel,
        getPanelSide
      }}
    >
      {children}
    </PanelLayoutContext.Provider>
  );
}

export function usePanelLayout() {
  const context = useContext(PanelLayoutContext);
  if (!context) {
    throw new Error('usePanelLayout must be used within a PanelLayoutProvider');
  }
  return context;
}
